import React, { useState } from 'react';
import { colors } from '../styles/colors';

interface ResendAppliesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  opportunityName: string;
  mode: 'all' | 'first_n';
  limit?: number;
}

const ResendAppliesModal: React.FC<ResendAppliesModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  opportunityName,
  mode,
  limit
}) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm();
      onClose();
    } catch (error) {
      // Error is handled by the parent component.
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  const amountText = mode === 'first_n'
    ? `as primeiras ${limit || 1} candidaturas aprovadas pelos filtros`
    : 'todas as candidaturas aprovadas pelos filtros';

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <span style={styles.icon}>📨</span>
          </div>
          <h2 style={styles.title}>Reenviar candidaturas</h2>
        </div>

        <div style={styles.content}>
          <div style={styles.infoBox}>
            Esta ação colocará as candidaturas novamente na fila de envio.
          </div>

          <p style={styles.message}>
            Você está prestes a reenviar <strong>{amountText}</strong> da vaga:
          </p>

          <div style={styles.opportunityName}>
            "{opportunityName}"
          </div>

          <div style={styles.consequencesList}>
            <p style={styles.consequencesTitle}>O que acontecerá:</p>
            <ul style={styles.consequences}>
              <li>Serão consideradas apenas candidaturas que passaram pelos filtros da vaga.</li>
              <li>O envio será processado pela fila do Trampos.</li>
              <li>O e-mail será enviado conforme o método de candidatura configurado na vaga.</li>
            </ul>
          </div>

          <p style={styles.finalInfo}>
            Confirme somente se deseja reenviar essas candidaturas ao recrutador.
          </p>

          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <span style={styles.loadingText}>Reenviando candidaturas...</span>
            </div>
          )}
        </div>

        <div style={styles.actions}>
          <button
            type="button"
            onClick={handleClose}
            style={{
              ...styles.cancelBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              ...styles.confirmBtn,
              ...(loading ? styles.disabledBtn : {})
            }}
            disabled={loading}
          >
            {loading ? 'Reenviando...' : 'Reenviar candidaturas'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1003,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '540px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
  },
  header: {
    padding: '2rem 2rem 1rem 2rem',
    textAlign: 'center',
    backgroundColor: '#F0F9FF',
  },
  iconContainer: {
    marginBottom: '1rem',
  },
  icon: {
    fontSize: '3rem',
    display: 'block',
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  content: {
    padding: '1.5rem 2rem',
  },
  infoBox: {
    padding: '1rem',
    backgroundColor: '#F0F9FF',
    border: `2px solid ${colors.primary}`,
    borderRadius: '8px',
    color: colors.primary,
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
  },
  message: {
    margin: '0 0 1rem 0',
    color: colors.text,
    fontSize: '1rem',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  opportunityName: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    border: `2px solid ${colors.primary}`,
    color: colors.primary,
    fontWeight: 'bold',
    fontSize: '1.1rem',
    margin: '1rem 0 1.5rem 0',
    textAlign: 'center',
    overflowWrap: 'anywhere',
  },
  consequencesList: {
    marginBottom: '1.5rem',
  },
  consequencesTitle: {
    margin: '0 0 0.5rem 0',
    color: colors.text,
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  consequences: {
    margin: 0,
    paddingLeft: '1.5rem',
    color: colors.textLight,
    fontSize: '0.9rem',
    lineHeight: '1.6',
  },
  finalInfo: {
    margin: 0,
    color: colors.primary,
    fontSize: '0.95rem',
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: '#F0F9FF',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    padding: '1.5rem 2rem 2rem 2rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.white,
    color: colors.textLight,
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 500,
  },
  confirmBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: `2px solid ${colors.primary}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: `2px solid ${colors.border}`,
    borderTop: `2px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  disabledBtn: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default ResendAppliesModal;
